import React, { FormEvent, useEffect, useState } from "react";

import Pagination from "../../components/common/Pagination";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import { createFaq, deleteFaq, fetchFaqs, updateFaq } from "../../store/slices/faqsSlice";
import { FaqCategory } from "../../types/admin";
import { formatDate, formatInquiryType, INQUIRY_TYPE_OPTIONS } from "../../utils/admin";

const FaqsPage = () => {
  const dispatch = useAppDispatch();
  const { list, totalCount, page, pageSize, loadingList, listError } = useAppSelector((state) => state.faqs);

  const [pageState, setPageState] = useState(1);
  const [query, setQuery] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [isVisibleFilter, setIsVisibleFilter] = useState("");

  const [editingId, setEditingId] = useState<number | null>(null);
  const [category, setCategory] = useState<"" | FaqCategory>("");
  const [question, setQuestion] = useState("");
  const [answer, setAnswer] = useState("");
  const [sortOrder, setSortOrder] = useState("");
  const [isVisible, setIsVisible] = useState(true);

  useEffect(() => {
    dispatch(
      fetchFaqs({
        q: query || undefined,
        is_visible: isVisibleFilter ? isVisibleFilter === "true" : undefined,
        page: pageState,
        page_size: 20,
      })
    );
  }, [dispatch, isVisibleFilter, pageState, query]);

  const resetForm = () => {
    setEditingId(null);
    setCategory("");
    setQuestion("");
    setAnswer("");
    setSortOrder("");
    setIsVisible(true);
  };

  const onSearch = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setQuery(searchInput.trim());
    setPageState(1);
  };

  const onSubmitFaq = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!question.trim() || !answer.trim()) {
      return;
    }

    const payload = {
      category: category || null,
      question: question.trim(),
      answer: answer.trim(),
      sort_order: sortOrder ? Number(sortOrder) : undefined,
      is_visible: isVisible,
    };

    try {
      if (editingId) {
        await dispatch(updateFaq({ faqId: editingId, payload })).unwrap();
        window.alert("FAQ가 성공적으로 수정되었습니다.");
      } else {
        await dispatch(createFaq(payload)).unwrap();
        window.alert("FAQ가 성공적으로 등록되었습니다.");
      }

      resetForm();
      dispatch(
        fetchFaqs({
          q: query || undefined,
          is_visible: isVisibleFilter ? isVisibleFilter === "true" : undefined,
          page: pageState,
          page_size: 20,
        })
      );
    } catch (error) {
      window.alert((error as string) || "저장에 실패했습니다.");
    }
  };

  return (
    <section className="admin-page faqs-page">
      <h2 className="section-title">자주 묻는 질문(FAQ)</h2>

      <form className="faq-editor" onSubmit={onSubmitFaq}>
        <input
          className="faq-editor__question"
          placeholder="질문"
          value={question}
          onChange={(event) => setQuestion(event.target.value)}
          required
        />
        <textarea
          className="faq-editor__answer"
          placeholder="답변"
          value={answer}
          onChange={(event) => setAnswer(event.target.value)}
          required
        />
        <div className="faq-editor__row">
          <label className="faq-editor__field">
            <span>분류</span>
            <select
              value={category}
              onChange={(event) => setCategory(event.target.value as "" | FaqCategory)}
            >
              <option value="">전체</option>
              {INQUIRY_TYPE_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>
          <label className="faq-editor__field">
            <span>정렬</span>
            <input
              type="number"
              min={0}
              placeholder="정렬 순서(선택)"
              value={sortOrder}
              onChange={(event) => setSortOrder(event.target.value)}
            />
          </label>
          <label className="faq-editor__field faq-editor__field--narrow">
            <span className="visually-hidden">노출</span>
            <select
              value={String(isVisible)}
              onChange={(event) => setIsVisible(event.target.value === "true")}
            >
              <option value="true">노출</option>
              <option value="false">숨김</option>
            </select>
          </label>
        </div>
        <button className="faq-editor__submit" type="submit">
          {editingId ? "FAQ 수정" : "FAQ 등록"}
        </button>
        {editingId && (
          <button className="faq-editor__cancel" type="button" onClick={resetForm}>
            수정 취소
          </button>
        )}
      </form>

      <div className="faq-list-toolbar">
        <select
          value={isVisibleFilter}
          onChange={(event) => {
            setIsVisibleFilter(event.target.value);
            setPageState(1);
          }}
        >
          <option value="">전체 노출 상태</option>
          <option value="true">노출</option>
          <option value="false">숨김</option>
        </select>
        <form className="faq-list-search" onSubmit={onSearch}>
          <input
            placeholder="질문/답변 검색"
            value={searchInput}
            onChange={(event) => setSearchInput(event.target.value)}
          />
          <button type="submit">검색</button>
        </form>
      </div>

      <div className="table-shell faq-table-shell">
        <table className="admin-table faq-table">
          <thead>
            <tr>
              <th>ID</th>
              <th>질문</th>
              <th>분류</th>
              <th>정렬</th>
              <th>노출</th>
              <th>수정일</th>
              <th>관리</th>
            </tr>
          </thead>
          <tbody>
            {list.map((faq) => (
              <tr key={faq.faq_id}>
                <td>{faq.faq_id}</td>
                <td className="text-left">{faq.question}</td>
                <td>{faq.category ? formatInquiryType(faq.category) : "전체"}</td>
                <td>{faq.sort_order}</td>
                <td>{faq.is_visible ? "노출" : "숨김"}</td>
                <td>{formatDate(faq.updated_at)}</td>
                <td>
                  <div className="table-actions">
                    <button
                      className="table-action-btn"
                      type="button"
                      onClick={() => {
                        setEditingId(faq.faq_id);
                        setCategory(faq.category ?? "");
                        setQuestion(faq.question);
                        setAnswer(faq.answer);
                        setSortOrder(String(faq.sort_order));
                        setIsVisible(faq.is_visible);
                        window.scrollTo({ top: 0, behavior: "smooth" });
                      }}
                    >
                      수정
                    </button>
                    <button
                      className="table-action-btn danger"
                      type="button"
                      onClick={async () => {
                        if (window.confirm("정말로 이 FAQ를 삭제하시겠습니까?")) {
                          try {
                            await dispatch(deleteFaq(faq.faq_id)).unwrap();
                            window.alert("FAQ가 삭제되었습니다.");
                            dispatch(
                              fetchFaqs({
                                q: query || undefined,
                                is_visible: isVisibleFilter ? isVisibleFilter === "true" : undefined,
                                page: pageState,
                                page_size: 20,
                              })
                            );
                          } catch (error) {
                            window.alert((error as string) || "삭제에 실패했습니다.");
                          }
                        }
                      }}
                    >
                      삭제
                    </button>
                  </div>
                </td>
              </tr>
            ))}
            {!loadingList && list.length === 0 && (
              <tr>
                <td className="empty-row" colSpan={7}>
                  등록된 FAQ가 없습니다.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {loadingList && <p className="loading-text">FAQ 목록을 불러오는 중입니다...</p>}
      {listError && <p className="error-text">{listError}</p>}
      <Pagination page={page || pageState} pageSize={pageSize || 20} totalCount={totalCount} onPageChange={setPageState} />
    </section>
  );
};

export default FaqsPage;
